import { apiRequest } from '@/api/apiClient/base';
import type {
  MySubmissionsResponse,
  UnitDetailResponse,
  UnitReleaseResponse,
  UnitsResponse,
  UserProfileResponse,
} from '@/api/types';

/** The index ∪ reviews union that backs browse and the home page. */
export function getUnits(): Promise<UnitsResponse> {
  return apiRequest<UnitsResponse>('/api/units');
}

/**
 * The signed-in caller's own submissions across every state — published,
 * unpublished, and in review. Session-scoped (the `nori_session` cookie), so it
 * backs the account page without leaning on the public `/api/units` union.
 */
export function getMySubmissions(): Promise<MySubmissionsResponse> {
  return apiRequest<MySubmissionsResponse>('/api/users/me/submissions');
}

/**
 * One unit by its full path (e.g. `skills/foo`, `skillsets/bar`). The server
 * matches the path segment **raw/undecoded**, so it is appended verbatim — a
 * unit path is already a slug of ref-safe segments and must not be
 * `encodeURIComponent`d (that would turn the `/` into `%2F` and miss).
 */
export function getUnit(path: string): Promise<UnitDetailResponse> {
  return apiRequest<UnitDetailResponse>(`/api/units/${path}`);
}

/**
 * One release of a unit, read from its release tree. `version` is an exact
 * version or `latest` (the highest stable release, else the highest
 * prerelease). The path is appended verbatim, as for `getUnit`.
 */
export function getRelease(
  path: string,
  version: string,
): Promise<UnitReleaseResponse> {
  return apiRequest<UnitReleaseResponse>(
    `/api/units/${path}/releases/${encodeURIComponent(version)}`,
  );
}

/**
 * A person's public profile by their login: their identity plus the units they
 * own that the viewer may see. Unlike a unit path, a login is a single segment
 * (and may be an email), so it is `encodeURIComponent`d — the server decodes it.
 */
export function getUserProfile(login: string): Promise<UserProfileResponse> {
  return apiRequest<UserProfileResponse>(
    `/api/users/${encodeURIComponent(login)}/profile`,
  );
}
